import { useAtom, useAtomValue, useSetAtom } from "jotai";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import { YES_OR_NO_OPTIONS } from "components/ButtonGroup";
import { POINT_OF_IMPACT_TRANSLATION_KEYS } from "components/DJVehicleDamageLocation/constants";
import Flex from "components/Flex";
import { NoInternetIcon } from "components/Icons/NoInternet";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { DigitalJourneyRoute } from "app/DigitalJourneyAppFlow/constants";
import { cityOfAccidentAtom, countryOfAccidentAtom, provinceOfAccidentAtom } from "atoms/accidentLocation";
import { additionalDocumentsAtom } from "atoms/additionalDocuments";
import { authoritiesPhotosAtom } from "atoms/authoritiesPhotos";
import { caiPhotosAtom } from "atoms/caiPhotos";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { injuredPeoplePhotosAtom } from "atoms/injuredPeoplePhotos";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { bodyShopAtom } from "atoms/selectBodyShop";
import { dateOfAccidentAtom, timeOfAccidentAtom } from "atoms/timeOfAccident";
import { typeOfClaimAtom } from "atoms/typeOfClaim";
import { vandalismSeverityAtom } from "atoms/vandalismSeverity";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { Checkpoint } from "types/Checkpoint";
import { AccidentType, ClaimHandlingDecision, VandalismSeverity, VehicleType } from "types/DigitalJourneySession";

import { getVehicleCircumstanceOptions } from "./DJAccidentCircumstancePage";
import {
  getAmicableDocSigned,
  getAuthoritiesInvolved,
  getDriverPresentInVehicle,
  getNumberOfPeopleInjured,
  getNumberOfVehiclesInvolved,
} from "./DJAccidentInformationPage";
import { getCountries } from "./DJAccidentTimeAndLocationPage";
import { getPersonRole } from "./DJInjuredPeopleDetailsPage";
import { getWindowsPointOfImpact } from "./DJSelectWindowsDamaged";
import { getInsuranceToActivate } from "./DJTypeOfClaimInsuranceToActivate";
import { getNaturalEvents } from "./DJTypeOfClaimNaturalEvents";
import { djTypeOfClaimOptions } from "./DJTypeOfClaimPage";
import { getVandalismSeverity } from "./DJTypeOfClaimTheftFire";
import DJPhotosSection from "./Summary/DJPhotosSection";
import DJSeparator from "./Summary/DJSeparator";
import DJSummarySection from "./Summary/DJSummarySection";
import { useDigitalJourneyInformation } from "./hooks/useDigitalJourneyInformation";
import { signatureImageAtom } from 'atoms/signature';
import { processSignatureForUpload } from 'utils/digitalJourney/signatureHelper';
import { useState } from "react";
import { SignatureModal } from "components/DJSignatureModal/DJSignatureModal";

export const DJClaimSummaryPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext } = useDigitalJourneyPageRouting();
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const toggleDrawer = useSetAtom(pageDrawerAtom);
  const { digitalJourney } = useDigitalJourneyInformation();
  const countryOfAccident = useAtomValue(countryOfAccidentAtom);
  const provinceOfAccident = useAtomValue(provinceOfAccidentAtom);
  const cityOfAccident = useAtomValue(cityOfAccidentAtom);
  const dateOfAccident = useAtomValue(dateOfAccidentAtom);
  const timeOfAccident = useAtomValue(timeOfAccidentAtom);
  const caiPhotos = useAtomValue(caiPhotosAtom);
  const injuredPeoplePhotos = useAtomValue(injuredPeoplePhotosAtom);
  const authoritiesPhotos = useAtomValue(authoritiesPhotosAtom);
  const additionalDocuments = useAtomValue(additionalDocumentsAtom);
  const selectedBodyShop = useAtomValue(bodyShopAtom);
  const damagePhotos = useAtomValue(damagePhotosAtom);
  const vehiclePhotos = useAtomValue(vehiclePhotosAtom);
  const typeOfClaim = useAtomValue(typeOfClaimAtom);
  const vandalismSeverity = useAtomValue(vandalismSeverityAtom);

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [, setSignatureImage] = useAtom(signatureImageAtom);

  const getAccidentDate = () => {
    if (dateOfAccident) {
      const date = new Date(dateOfAccident);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
    }
    return "";
  };

  const getAccidentTime = () => {
    if (timeOfAccident) {
      const date = new Date(timeOfAccident);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${hours}:${minutes}`;
    }
    return "";
  };

  const onContinue = () => {
    if (typeOfClaim === AccidentType.COLLISION_BETWEEN_VEHICLES) {
      setSignatureModalOpen(true);
    } else {
      toggleDrawer(false);
      trackEvent({ name: "Digital Journey Claim Summary - continue" });
      onNext();
    }
  };

  const onSignatureModalClose = () => {
    setSignatureModalOpen(false);
    toggleDrawer(false);
    trackEvent({ name: "Digital Journey Claim Summary - continue without signature" });
    onNext();
  }

  const handleSignatureSubmit = async (base64: string) => {
    const image = await processSignatureForUpload(base64);
    setSignatureImage(image);
    toggleDrawer(false);
    trackEvent({ name: "Digital Journey Claim Summary - continue with signature" });
    onNext();
  };

  const isDisabled = !digitalJourney;

  return (
    <PageLayout
      title="Verifica le informazioni inserite"
      dataTestId="djClaimSummary"
      forceContinue={targetReached}
      bottomButtons={
        <Button disabled={isDisabled} onClick={onContinue}>
          Invia
        </Button>
      }
    >
      {digitalJourney && (
        <Flex px={16} my={24}>
          <DJSummarySection
            title="Targa e modello del veicolo"
            pageRoute={DigitalJourneyRoute.VehicleType}
            pageName="Digital Journey Select Vehicle"
            values={[
              { label: "Targa del tuo veicolo", value: digitalJourney.licensePlatePh },
              {
                label: "Tipo",
                value: digitalJourney.vehicleTypePh === VehicleType.CAR ? "Auto" : "Moto",
              },
            ]}
          />
          <DJSeparator />
          <DJSummarySection
            title="Luogo e data del sinistro"
            pageRoute={DigitalJourneyRoute.AccidentTimeAndLocation}
            pageName="Digital Journey Accident Time and Location"
            values={[
              { label: "Data del sinistro", value: getAccidentDate() },
              { label: "Ora del sinistro", value: getAccidentTime() },
              {
                label: "Nazione",
                value: getCountries(t).find((option) => option.value === countryOfAccident)?.label,
              },
              { label: "Provincia", value: provinceOfAccident },
              { label: "Città", value: cityOfAccident?.city },
            ]}
          />
          <DJSeparator />
          <DJSummarySection
            title="Tipologia di sinistro"
            pageRoute={DigitalJourneyRoute.TypeOfClaim}
            pageName="Digital Journey Type of Claim"
            values={[
              {
                label: "Tipologia",
                value: djTypeOfClaimOptions(t).find((option) => option.value === digitalJourney.accidentType)?.label,
              },
            ]}
          />
          {typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Informazioni sul sinistro"
                pageRoute={DigitalJourneyRoute.TypeOfClaimTheftFire}
                pageName="Digital Journey Type of Claim Fire Theft Vandalism"
                values={[
                  {
                    label: "Intervento delle Autorità",
                    value: getAuthoritiesInvolved(t).find(
                      (option) => option.value === digitalJourney.authoritiesIntervention
                    )?.label,
                  },
                  {
                    label: "Garanzia attivata",
                    value: getVandalismSeverity(t).find((option) => option.value === digitalJourney.vandalismSeverity)
                      ?.label,
                  },
                ]}
              />
            </>
          )}
          {typeOfClaim === AccidentType.NATURAL_EVENTS && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Informazioni sul sinistro"
                pageRoute={DigitalJourneyRoute.TypeOfClaimNaturalEvent}
                pageName="Digital Journey Type of Claim Natural Events"
                values={[
                  {
                    label: "Tipo di evento",
                    value: getNaturalEvents(t).find((option) => option.value === digitalJourney.naturalEventType)
                      ?.label,
                  },
                ]}
              />
            </>
          )}
          {typeOfClaim === AccidentType.KASKO && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Informazioni sul sinistro"
                pageRoute={DigitalJourneyRoute.TypeOfClaimInsuranceToActivate}
                pageName="Digital Journey Type of Claim Insurance to Activate"
                values={[
                  {
                    label: "Tipo di garanzia da attivare",
                    value: getInsuranceToActivate(t).find((option) => option.value === digitalJourney.insuranceType)
                      ?.label,
                  },
                ]}
              />
            </>
          )}
          {typeOfClaim === AccidentType.COLLISION_BETWEEN_VEHICLES && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Informazioni sul sinistro"
                pageRoute={DigitalJourneyRoute.AccidentInformation}
                pageName="Digital Journey Accident Information"
                values={[
                  {
                    label: "Veicoli coinvolti",
                    value: getNumberOfVehiclesInvolved(t).find(
                      (option) => option.value === String(digitalJourney.numVehiclesInvolved)
                    )?.label,
                  },
                  {
                    label: "Presenza di un conducente",
                    value: getDriverPresentInVehicle(t).find((option) => option.value === digitalJourney.driverPresent)
                      ?.label,
                  },
                  {
                    label: "Informazioni sull'altro veicolo",
                    value: t(
                      YES_OR_NO_OPTIONS.find((option) => option.value === digitalJourney.informationOnOtherDriver)
                        ?.label || ""
                    ),
                  },
                  {
                    label: "Persone ferite",
                    value: getNumberOfPeopleInjured(t).find(
                      (option) => option.value === String(digitalJourney.numInjuredPeople)
                    )?.label,
                  },
                  {
                    label: "CAI firmato",
                    value: getAmicableDocSigned(t).find((option) => option.value === digitalJourney.cai)?.label,
                  },
                  {
                    label: "Intervento delle Autorità",
                    value: getAuthoritiesInvolved(t).find(
                      (option) => option.value === digitalJourney.authoritiesIntervention
                    )?.label,
                  },
                ]}
              />
            </>
          )}
          {typeOfClaim === AccidentType.COLLISION_BETWEEN_VEHICLES && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Dinamica del sinistro"
                pageRoute={DigitalJourneyRoute.AccidentCircumstance}
                pageName="Digital Journey Accident Circumstance"
                values={[
                  {
                    label: "Dinamica del tuo veicolo",
                    value: getVehicleCircumstanceOptions(t).find(
                      (option) => option.value === digitalJourney.vehicleCircumstancePh
                    )?.label,
                  },
                  {
                    label: "Dinamica dell'altro veicolo",
                    value: getVehicleCircumstanceOptions(t).find(
                      (option) => option.value === digitalJourney.otherVehicleCircumstance
                    )?.label,
                  },
                ]}
              />
            </>
          )}
          {typeOfClaim !== AccidentType.GLASS &&
            typeOfClaim !== AccidentType.NATURAL_EVENTS &&
            !(
              vandalismSeverity &&
              (vandalismSeverity === VandalismSeverity.TOTAL_FIRE ||
                vandalismSeverity === VandalismSeverity.TOTAL_THEFT)
            ) && (
              <>
                <DJSeparator />
                <DJSummarySection
                  title="Condizioni del veicolo"
                  pageRoute={DigitalJourneyRoute.VehicleCondition}
                  pageName="Digital Journey - Vehicle condition"
                  values={[
                    {
                      label: "Kilometraggio",
                      value: `${digitalJourney.mileage} ${t("vehicle_condition.mileage.unit")}`,
                    },
                    {
                      label: "Airbag esploso",
                      value: t(
                        YES_OR_NO_OPTIONS.find((option) => option.value === digitalJourney.airbagDeployed)?.label || ""
                      ),
                    },
                    {
                      label: "Veicolo marciante",
                      value: t(
                        YES_OR_NO_OPTIONS.find((option) => option.value === digitalJourney.isDrivable)?.label || ""
                      ),
                    },
                  ]}
                />
              </>
            )}
          {typeOfClaim !== AccidentType.GLASS && (digitalJourney.pointsOfImpactPh?.length || 0 > 0) && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Danni al tuo veicolo"
                pageRoute={DigitalJourneyRoute.DamageLocationPH}
                pageName="Digital Journey Policy Holder Damage Location"
                values={[
                  {
                    label: "Aree danneggiate",
                    value: (digitalJourney.pointsOfImpactPh || [])
                      .map((poi) => t(POINT_OF_IMPACT_TRANSLATION_KEYS[poi]))
                      .join(", "),
                  },
                ]}
              />
            </>
          )}
          {(typeOfClaim === AccidentType.COLLISION_BETWEEN_VEHICLES || typeOfClaim === AccidentType.KASKO) && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Informazioni sul conducente"
                pageRoute={DigitalJourneyRoute.PHPersonalDetails}
                pageName="Digital Journey Policy Holder Personal Details"
                values={[
                  {
                    label: "Nome",
                    value: digitalJourney.firstNamePh,
                  },
                  {
                    label: "Cognome",
                    value: digitalJourney.lastNamePh,
                  },
                  ...(digitalJourney.personalCodePh
                    ? [
                        {
                          label: "Codice fiscale",
                          value: digitalJourney.lastNamePh,
                        },
                      ]
                    : []),
                ]}
              />
            </>
          )}
          {typeOfClaim === AccidentType.COLLISION_BETWEEN_VEHICLES && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Informazioni sull'altro veicolo"
                pageRoute={DigitalJourneyRoute.OtherDriverPersonalDetails}
                pageName="Digital Journey Other Driver Details"
                values={[
                  {
                    label: "Targa",
                    value: digitalJourney.otherVehicleLicensePlate,
                  },
                  {
                    label: "Tipo di veicolo",
                    value: digitalJourney.otherVehicleType === VehicleType.CAR ? "Auto" : "Moto",
                  },
                  ...(digitalJourney.otherVehicleInsurance
                    ? [
                        {
                          label: "Assicurazione",
                          value: digitalJourney.otherVehicleInsurance,
                        },
                      ]
                    : []),
                  ...(digitalJourney.otherDriverFirstName
                    ? [
                        {
                          label: "Nome del conducente",
                          value: digitalJourney.otherDriverFirstName,
                        },
                      ]
                    : []),
                  ...(digitalJourney.otherDriverLastName
                    ? [
                        {
                          label: "Cognome del conducente",
                          value: digitalJourney.otherDriverLastName,
                        },
                      ]
                    : []),
                  ...(digitalJourney.otherDriverPersonalCode
                    ? [
                        {
                          label: "Codice fiscale ",
                          value: digitalJourney.otherDriverPersonalCode,
                        },
                      ]
                    : []),
                ]}
              />
            </>
          )}
          {typeOfClaim === AccidentType.GLASS && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Cristalli danneggiati"
                pageRoute={DigitalJourneyRoute.SelectWindowsDamaged}
                pageName="Digital Journey Select Windows Damaged"
                values={[
                  {
                    label: "Cristalli danneggiati",
                    value: (digitalJourney.pointsOfImpactGlass || [])
                      .map((poi) => getWindowsPointOfImpact(t).find((option) => option.value === poi)?.label)
                      .join(", "),
                  },
                ]}
              />
            </>
          )}
          {digitalJourney.otherVehiclePointsOfImpact && digitalJourney.otherVehiclePointsOfImpact.length > 0 && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Danni all'altro veicolo"
                pageRoute={DigitalJourneyRoute.DamageLocationOther}
                pageName="Digital Journey Other Vehicle Damage Location"
                values={[
                  {
                    label: "Aree danneggiate",
                    value: (digitalJourney.otherVehiclePointsOfImpact || [])
                      .map((poi) => t(POINT_OF_IMPACT_TRANSLATION_KEYS[poi]))
                      .join(", "),
                  },
                ]}
              />
            </>
          )}
          {digitalJourney.injuredPersons &&
            digitalJourney.injuredPersons.length > 0 &&
            digitalJourney.injuredPersons.map((injuredPerson) => (
              <>
                <DJSeparator />
                <DJSummarySection
                  title="Informazioni sul ferito 1"
                  pageRoute={DigitalJourneyRoute.InjuredPeopleDetails}
                  pageName="Digital Journey Injured People Details"
                  values={[
                    {
                      label: "Nome del ferito",
                      value: injuredPerson.firstName,
                    },
                    {
                      label: "Cognome del ferito",
                      value: injuredPerson.lastName,
                    },
                    ...(injuredPerson.personalCode
                      ? [
                          {
                            label: "Codice fiscale ",
                            value: injuredPerson.personalCode,
                          },
                        ]
                      : []),
                    {
                      label: "Ruolo",
                      value: getPersonRole(t).find((option) => option.value === injuredPerson.role)?.label,
                    },
                  ]}
                />
              </>
            ))}
          {caiPhotos && caiPhotos.length > 0 && (
            <>
              <DJSeparator />
              <DJPhotosSection
                title="Fotografie del CAI"
                pageRoute={DigitalJourneyRoute.CAIPhotos}
                pageName="Digital Journey CAI Photos"
                photos={caiPhotos}
              />
            </>
          )}
          {injuredPeoplePhotos && injuredPeoplePhotos.length > 0 && (
            <>
              <DJSeparator />
              <DJPhotosSection
                title="Fotografie del referto Pronto Socorso"
                pageRoute={DigitalJourneyRoute.InjuredPeopleDocumentPhotos}
                pageName="Digital Journey Injured People Photos"
                photos={injuredPeoplePhotos}
              />
            </>
          )}
          {authoritiesPhotos && authoritiesPhotos.length > 0 && (
            <>
              <DJSeparator />
              <DJPhotosSection
                title="Fotografie dei documenti delle Autorità"
                pageRoute={DigitalJourneyRoute.AuthoritiesDocumentsPhotos}
                pageName="Digital Journey Authorities Document Photos"
                photos={authoritiesPhotos}
              />
            </>
          )}
          {additionalDocuments && additionalDocuments.length > 0 && (
            <>
              <DJSeparator />
              <DJPhotosSection
                title="Fotografie dei documenti delle Autorità"
                pageRoute={DigitalJourneyRoute.AdditionalDocumentsPhotos}
                pageName="Digital Journey Additional Documents Photos"
                photos={additionalDocuments}
              />
            </>
          )}
          {digitalJourney.additionalDetails && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Ulteriori dettagli"
                pageRoute={DigitalJourneyRoute.AdditionalInformation}
                pageName="Digital Journey Additional Information"
                values={[
                  {
                    label: "",
                    value: digitalJourney.additionalDetails,
                  },
                ]}
              />
            </>
          )}
          {digitalJourney.claimHandlingDecision && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Come vuoi procedere"
                pageRoute={DigitalJourneyRoute.HandlingDecision}
                pageName="Digital Journey Claim Handling Decision"
                values={[
                  {
                    label: "Gestione del sinistro",
                    value:
                      digitalJourney.claimHandlingDecision === ClaimHandlingDecision.BODY_SHOP
                        ? "Carrozzeria convenzionata"
                        : digitalJourney.claimHandlingDecision === ClaimHandlingDecision.EXPERT
                        ? "Perito"
                        : "Raccolta fotografica",
                  },
                ]}
              />
            </>
          )}
          {digitalJourney.claimHandlingDecision === ClaimHandlingDecision.BODY_SHOP && selectedBodyShop && (
            <>
              <DJSeparator />
              <DJSummarySection
                title="Carrozzeria scelta"
                pageRoute={DigitalJourneyRoute.SelectBodyShop}
                pageName="Digital Journey Select Body Shop"
                values={[
                  {
                    label: "Nome",
                    value: selectedBodyShop.name,
                  },
                  {
                    label: "Indirizzo",
                    value: selectedBodyShop.address,
                  },
                  {
                    label: "CAP",
                    value: selectedBodyShop.postalCode,
                  },
                  {
                    label: "Città",
                    value: selectedBodyShop.city,
                  },
                ]}
              />
            </>
          )}
          {digitalJourney.claimHandlingDecision === ClaimHandlingDecision.PHOTO_CAPTURE &&
            damagePhotos &&
            vehiclePhotos && (
              <>
                <DJSeparator />
                <DJPhotosSection
                  title="Fotografie dei danni"
                  pageRoute={DigitalJourneyRoute.DamagePhotoCapture}
                  pageName={t("damage_photos.header")}
                  photos={damagePhotos}
                />
                <DJSeparator />
                <DJPhotosSection
                  title="Fotografie degli angoli"
                  pageRoute={DigitalJourneyRoute.VehiclePhotoCapture}
                  pageName={t("vehicle_corner_photos.header")}
                  photos={vehiclePhotos}
                />
              </>
            )}
        </Flex>
      )}
      <SignatureModal
        opened={signatureModalOpen}
        onClose={() => onSignatureModalClose()}
        onSubmit={handleSignatureSubmit}
      />
      <BottomDrawer
        heading={t("offline.header")}
        headingIcon={<NoInternetIcon />}
        callToActionOnClick={onContinue}
        callToActionText={t("offline.upload_photos")}
      >
        <Text sizeVariant="md">{t("offline.instruction")}</Text>
      </BottomDrawer>
    </PageLayout>
  );
};
